import { useState } from "react";
import { Typography, Input, Button, Card, Alert, Space } from "antd";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "@/config/firebase";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

const functions = getFunctions(app, "europe-west1");

const { Title, Text } = Typography;

const validateBetaCodeFn = httpsCallable<
  { code: string },
  { valid: boolean; reason?: string }
>(functions, "validateBetaCode");

export default function BetaPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!code.trim()) return;

    if (!user) {
      // Store code and redirect to login; after login the user comes back
      sessionStorage.setItem("pendingBetaCode", code.trim().toUpperCase());
      navigate("/login", { state: { from: { pathname: "/beta" } } });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await validateBetaCodeFn({ code: code.trim() });
      if (result.data.valid) {
        setSuccess(true);
        setTimeout(() => navigate("/"), 2000);
      } else {
        const reasons: Record<string, string> = {
          not_found: "Invalid code. Please check and try again.",
          already_used: "This code has already been used.",
          revoked: "This code is no longer valid.",
        };
        setError(reasons[result.data.reason || ""] || "Invalid code.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-validate if user just logged in with a pending code
  const pendingCode = sessionStorage.getItem("pendingBetaCode");
  if (user && pendingCode && !success && !loading && !error) {
    sessionStorage.removeItem("pendingBetaCode");
    setCode(pendingCode);
    setTimeout(() => {
      setLoading(true);
      validateBetaCodeFn({ code: pendingCode }).then((result) => {
        if (result.data.valid) {
          setSuccess(true);
          setTimeout(() => navigate("/"), 2000);
        } else {
          setError("Invalid or expired code.");
        }
      }).catch(() => {
        setError("Something went wrong. Please try again.");
      }).finally(() => setLoading(false));
    }, 0);
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5", padding: 24 }}>
      <Card style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>Join the Beta</Title>
            <Text type="secondary">Enter your invite code to get free access</Text>
          </div>

          {success ? (
            <Alert
              type="success"
              message="Welcome to the beta!"
              description="Your account is now active. Redirecting..."
              showIcon
            />
          ) : (
            <>
              <Input
                size="large"
                placeholder="Enter code"
                value={code}
                onChange={(e) => { setCode(e.target.value); setError(null); }}
                onPressEnter={handleSubmit}
                maxLength={12}
                style={{ textAlign: "center", letterSpacing: 2, textTransform: "uppercase" }}
              />

              {error && <Alert type="error" message={error} showIcon />}

              <Button
                type="primary"
                size="large"
                block
                onClick={handleSubmit}
                loading={loading}
                disabled={!code.trim()}
              >
                {user ? "Activate" : "Continue"}
              </Button>

              {!user && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  You'll need to sign in or create an account to continue.
                </Text>
              )}
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
